import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { analyzeWebsite, latestWebsiteAnalysis } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const CAT_LABELS = {
  seo: "SEO", speed: "Speed", accessibility: "Accessibility", marketing: "Marketing",
  social_media: "Social Media", email_capture: "Email Capture", google_presence: "Google Presence",
  local_seo: "Local SEO", content_quality: "Content Quality",
};
const scoreColor = (v) => (v >= 75 ? "text-emerald-400" : v >= 50 ? "text-amber-300" : "text-red-400");
const barColor = (v) => (v >= 75 ? "bg-emerald-500" : v >= 50 ? "bg-amber-400" : "bg-red-500");

export const WebsiteAnalyzer = () => {
  const { activeId, activeClient } = useClient();
  const qc = useQueryClient();
  const [url, setUrl] = useState("");
  const { data: report } = useQuery({
    queryKey: ["website-analysis", activeId],
    queryFn: () => latestWebsiteAnalysis(activeId), enabled: !!activeId,
  });
  const run = useMutation({
    mutationFn: () => analyzeWebsite({ client_id: activeId, url: url || activeClient?.website || "" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["website-analysis", activeId] }); toast.success("Website analysis complete"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Analysis failed"),
  });
  const r = report?.id ? report : null;

  return (
    <div className="card-matte p-5 mt-5" data-testid="website-analyzer">
      <div className="flex items-center gap-2 mb-1">
        <Globe size={16} className="brand-text" weight="fill" />
        <span className="font-head font-semibold">Full Website Analysis</span>
      </div>
      <p className="text-xs text-[#9a9a9a] mb-4">Enter any website — the AI fetches it live and scores SEO, speed, accessibility, marketing, social, email capture, Google presence, local SEO and content quality.</p>
      <div className="flex flex-wrap gap-2 mb-5">
        <Input data-testid="website-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder={activeClient?.website || "https://yourbusiness.com"} className="bg-[#10101A] border-white/10 h-10 flex-1 min-w-[220px]" />
        <Button data-testid="website-analyze-btn" onClick={() => run.mutate()} disabled={run.isPending || !activeId || (!url.trim() && !activeClient?.website)}
          className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white rounded-full h-10 px-5">
          <MagnifyingGlass size={15} className="mr-1.5" /> {run.isPending ? "Analyzing… (~30s)" : "Analyze Website"}
        </Button>
      </div>
      {r ? (
        <div data-testid="website-analysis-report">
          <div className="flex flex-wrap items-center gap-5 mb-4">
            <div className="text-center">
              <div className={`font-head font-extrabold text-5xl ${scoreColor(r.overall)}`} data-testid="website-overall-score">{r.overall}</div>
              <div className="text-[10px] text-[#7a7a7a] mt-0.5">overall / 100</div>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-xs text-[#7a7a7a] mb-1">{r.url} · {(r.created_at || "").slice(0, 10)} · {r.signals?.response_ms}ms response</div>
              <p className="text-sm text-[#cfcfcf]">{r.summary}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 mb-5" data-testid="website-category-scores">
            {Object.entries(r.scores || {}).map(([k, v]) => (
              <div key={k} data-testid={`website-score-${k}`}>
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="text-[#9a9a9a]">{CAT_LABELS[k] || k}</span>
                  <span className={`font-bold ${scoreColor(v)}`}>{v}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className={`h-full ${barColor(v)}`} style={{ width: `${v}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="label-caps mb-2">Prioritized recommendations</div>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="website-recommendations">
            {(r.recommendations || []).map((rec, i) => (
              <div key={i} className="rounded-lg border border-white/8 bg-white/[0.02] p-3 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${rec.priority === "high" ? "bg-red-500/15 text-red-300" : rec.priority === "medium" ? "bg-amber-500/15 text-amber-300" : "bg-white/10 text-[#9a9a9a]"}`}>{rec.priority}</span>
                  <span className="text-[#7a7a7a]">{CAT_LABELS[rec.category] || rec.category}</span>
                </div>
                <div className="text-[#cfcfcf] font-semibold">{rec.issue}</div>
                <div className="text-[#9a9a9a] mt-0.5">→ {rec.fix}</div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center text-xs text-[#5a5a5a] py-8" data-testid="website-analysis-empty">
          {run.isPending ? "Fetching the site and running the audit…" : "No analysis yet — run your first one above."}
        </div>
      )}
    </div>
  );
};
